/* eslint-disable camelcase */
import { v4 as uuid } from 'uuid';

/**
 * Generates a PKCE (Proof Key for Code Exchange) verifier/challenge pair.
 * The verifier is a random base64url string; the challenge is its SHA-256 hash.
 *
 * @returns {Promise<{verifier: string, challenge: string}>}
 */
export async function generatePKCEPair() {
    const NUM_OF_BYTES = 32;
    const HASH_ALG = 'SHA-256';

    const randomBytes = new Uint8Array(NUM_OF_BYTES);
    window.crypto.getRandomValues(randomBytes);
    const verifier = btoa(String.fromCharCode(...randomBytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hashBuffer = await window.crypto.subtle.digest(HASH_ALG, data);
    const challenge = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

    return { verifier, challenge };
}

/**
 * Substitutes `${variable}` placeholders in a string with values from `params`.
 * Also normalizes the escaped `${'$'}{variable}` form to `${variable}` first.
 * Non-string input is returned unchanged. Unknown placeholders are left intact.
 *
 * @param {string} str
 * @param {Object} params
 * @returns {string}
 */
export const substituteVariables = (str, params) => {
    if (typeof str !== 'string') {
        return str;
    }
    const normalized = str.replace(/\$\{['"]?\$['"]?\}\{([^}]+)\}/g, '${$1}');
    return normalized.replace(/\$\{([^}]+)\}/g, (match, parameter) => {
        return params[parameter] || match;
    });
};

/**
 * Opens a centered 360x480 popup for the authorization flow.
 * Returns the child window handle (or null if blocked).
 */
const openAuthPopup = url => {
    const w = 360;
    const h = 480;
    // Fixes dual-screen position
    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
        ? window.innerWidth
        : document.documentElement.clientWidth
          ? document.documentElement.clientWidth
          : screen.width;
    const height = window.innerHeight
        ? window.innerHeight
        : document.documentElement.clientHeight
          ? document.documentElement.clientHeight
          : screen.height;

    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft;
    const top = (height - h) / 2 / systemZoom + dualScreenTop;

    return window.open(
        url,
        '_blank',
        `scrollbars=yes,toolbar=0,status=0,width=${w / systemZoom},height=${
            h / systemZoom
        },top=${top},left=${left},rel=opener`
    );
};

const defaultFormatResult = ({ body, tokenData }) => ({ ...body, ...(tokenData || {}) });

// Upper bound on how long we wait for the OAuth popup to post its result back. Without this the
// `message` listener + poll interval would live forever if the user abandons the popup without
// closing it. Generous enough not to interrupt a real login (provider sign-in + MFA).
const OAUTH_POPUP_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Shared OAuth2 "connect to an integration" flow used by UC, Data Mapping, and Integrations.
 *
 * Supports both the `client_credentials` flow (no `authorizationUrl`, exchange credentials
 * directly for a token) and the `authorization_code` flow (open an auth popup, wait for the
 * redirect to `postMessage` the response back, validate `state`, then exchange the code).
 * PKCE is applied automatically whenever the authorization URL already contains `code_challenge`.
 *
 * @param {Object}   options
 * @param {string}   options.agentId                    Agent initiating the connection.
 * @param {string}   [options.authorizationUrl]         Authorization URL template. Omit for client_credentials.
 * @param {Object}   [options.params={}]                Credential params to substitute/send.
 * @param {string}   options.redirectPath               Path the OAuth provider redirects the popup to.
 * @param {boolean}  [options.appendCredentialsToUrl=false] When true, every param is set as an authorize-URL
 *                                                       query param (Integrations behavior). When false, only
 *                                                       params whose key already appears in the URL are set
 *                                                       (UC / Data Mapping behavior).
 * @param {boolean}  [options.usePKCE=true]             When true, a PKCE pair is generated; `code_challenge`
 *                                                       is added to the authorize URL when it requests it, and
 *                                                       `code_verifier` is sent in the exchange body. When false,
 *                                                       no PKCE is applied (UC behavior).
 * @param {Function} options.exchangeToken             (agentId, body) => Promise token-exchange service. Required
 *                                                       and passed explicitly by each caller so this helper stays
 *                                                       decoupled from any one feature's backend endpoint.
 * @param {Function} [options.formatResult]             ({ body, tokenData }) => any — shapes the resolved value.
 * @returns {Promise<any>}
 */
export const connectOAuth2 = async ({
    agentId,
    authorizationUrl,
    params = {},
    redirectPath,
    appendCredentialsToUrl = false,
    usePKCE = true,
    exchangeToken,
    formatResult = defaultFormatResult,
}) => {
    // exchangeToken is injected by each caller (kept out of b2b/common so this helper
    // stays decoupled from any feature's backend endpoint). Fail fast with a clear
    // message instead of a late TypeError if a caller forgets to pass it.
    if (typeof exchangeToken !== 'function') {
        return Promise.reject('connectOAuth2 requires an exchangeToken function.');
    }

    const redirectUri = new URL(
        redirectPath,
        `${document.location.protocol}//${document.location.host}`
    );

    const substitutedParams = Object.entries(params).reduce((acc, [key, value]) => {
        acc[key] = substituteVariables(value, params);
        return acc;
    }, {});

    // client_credentials flow — no popup, exchange the credentials directly.
    if (!authorizationUrl) {
        let codeVerifier;
        if (usePKCE) {
            ({ verifier: codeVerifier } = await generatePKCEPair());
        }
        const body = {
            ...substitutedParams,
            redirect_uri: redirectUri.href,
            ...(usePKCE && { code_verifier: codeVerifier }),
        };
        return exchangeToken(agentId, body).then(
            tokenData => formatResult({ body, tokenData }),
            ({ message } = {}) => Promise.reject(message)
        );
    }

    // authorization_code flow.
    // Build (and validate) the authorize URL first so an unconstructable URL rejects without
    // ever opening a popup. PKCE's code_challenge is appended later, after the async hash.
    let uri;
    try {
        uri = new URL(substituteVariables(authorizationUrl, params));
    } catch (e) {
        return Promise.reject(
            `The authorization url for OAuth Agent ${agentId} cannot be constructed.`
        );
    }

    const oauthState = uuid();

    // Substitute variables in existing URL search param values.
    uri.searchParams.forEach((value, key) => {
        const substitutedValue = substituteVariables(value, params);
        if (substitutedValue !== value) {
            uri.searchParams.set(key, substitutedValue);
        }
    });

    Object.entries(substitutedParams).forEach(([key, value]) => {
        if (appendCredentialsToUrl || uri.search.includes(key)) {
            uri.searchParams.set(key, value);
        }
    });

    if (uri.search.includes('redirect_uri')) {
        uri.searchParams.set('redirect_uri', redirectUri.href);
    }
    uri.searchParams.append('state', oauthState);

    // Open the popup synchronously — BEFORE the async PKCE hashing below — so the browser keeps
    // it tied to the user's click. Opening a window after an `await` trips popup blockers. It
    // starts at about:blank and we navigate it to the authorize URL once that URL is finalized.
    const child = openAuthPopup('about:blank');
    if (!child) {
        return Promise.reject(
            'The authentication popup was blocked. Please allow popups for this site and try again.'
        );
    }

    let codeVerifier;
    let codeChallenge;
    if (usePKCE) {
        ({ verifier: codeVerifier, challenge: codeChallenge } = await generatePKCEPair());
    }

    if (usePKCE && uri.search.includes('code_challenge')) {
        uri.searchParams.append('code_challenge', codeChallenge);
        uri.searchParams.append('code_challenge_method', 'S256');
    }

    return new Promise((resolve, reject) => {
        let timer;
        let timeout;
        let callback;

        const cleanup = () => {
            clearInterval(timer);
            clearTimeout(timeout);
            window.removeEventListener('message', callback);
        };

        callback = messageEvent => {
            const { origin, data } = messageEvent;
            if (origin !== window.location.origin) {
                // If the OAuth didn't redirect and post...ignore it
                return;
            }
            let parsedData;
            try {
                parsedData = typeof data === 'string' ? JSON.parse(data) : data || {};
            } catch (e) {
                // We cannot guarantee that this message was intended for OAuth, so don't reject
                return;
            }
            const { state, ...response } = parsedData;
            const { access_token } = response;
            if (!state || state !== oauthState) {
                // This message might be for another OAuth, so don't reject
                return;
            }
            cleanup();
            const body = {
                ...substitutedParams,
                ...response,
                redirect_uri: redirectUri.href,
                ...(usePKCE &&
                    uri.search.includes('code_challenge') && { code_verifier: codeVerifier }),
                state: uri.search.includes('state_long')
                    ? uri.searchParams.get('state_long')
                    : oauthState,
            };

            // Only do a token request if the OAuth was not `token`
            if (!access_token) {
                exchangeToken(agentId, body).then(
                    tokenData => resolve(formatResult({ body, tokenData })),
                    ({ message } = {}) => reject(message)
                );
            } else {
                resolve(formatResult({ body }));
            }
        };

        window.addEventListener('message', callback);
        child.location.href = uri.href;

        timer = setInterval(() => {
            if (child.closed) {
                cleanup();
                reject('Window was closed before authentication completed. Please try again.');
            }
        }, 500);

        // Don't leak the listener/interval forever if the user abandons the popup.
        timeout = setTimeout(() => {
            cleanup();
            try {
                if (!child.closed) {
                    child.close();
                }
            } catch (e) {
                // Popup already gone
            }
            reject('Authentication timed out before completing. Please try again.');
        }, OAUTH_POPUP_TIMEOUT_MS);
    });
};
